import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import i18n from "@/i18n";
import type { MedicalRecordInput } from "@/lib/validations/medical-record";

const nsMedical = "medicalRecords" as const;
const nsCommon = "common" as const;

const tm = (key: string) => i18n.t(key, { ns: nsMedical });
const tc = (key: string) => i18n.t(key, { ns: nsCommon });

const hasText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const hasNumber = (value: unknown): value is number =>
  typeof value === "number" && !Number.isNaN(value);

const formatDateTime = (value: Date) => {
  if (Number.isNaN(value.getTime())) return null;
  return format(value, "dd/MM/yyyy HH:mm", { locale: ptBR });
};

const formatDateOnly = (value: Date) => {
  if (Number.isNaN(value.getTime())) return null;
  return format(value, "dd/MM/yyyy", { locale: ptBR });
};

const fieldLine = (label: string, value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.includes("\n") ? `${label}:\n${trimmed}` : `${label}: ${trimmed}`;
};

type SectionDef = {
  sectionTitleKey: string;
  fields: Array<{
    getLabel: () => string;
    getValue: (v: MedicalRecordInput) => string | null;
  }>;
};

const sections: SectionDef[] = [
  {
    sectionTitleKey: "form.tabs.anamnesis",
    fields: [
      { getLabel: () => tm("form.chiefComplaint"), getValue: (v) => (hasText(v.chief_complaint) ? v.chief_complaint.trim() : null) },
      { getLabel: () => tm("form.history"), getValue: (v) => (hasText(v.history) ? v.history.trim() : null) },
      { getLabel: () => tm("form.behavior"), getValue: (v) => (hasText(v.behavior) ? v.behavior.trim() : null) },
      { getLabel: () => tm("form.diet"), getValue: (v) => (hasText(v.diet) ? v.diet.trim() : null) },
      { getLabel: () => tm("form.environment"), getValue: (v) => (hasText(v.environment) ? v.environment.trim() : null) },
    ],
  },
  {
    sectionTitleKey: "form.tabs.physicalExam",
    fields: [
      { getLabel: () => tm("form.weight"), getValue: (v) => (hasNumber(v.weight) ? String(v.weight) : null) },
      { getLabel: () => tm("form.temperature"), getValue: (v) => (hasNumber(v.temperature) ? String(v.temperature) : null) },
      { getLabel: () => tm("form.heartRate"), getValue: (v) => (hasNumber(v.heart_rate) ? String(v.heart_rate) : null) },
      { getLabel: () => tm("form.respiratoryRate"), getValue: (v) => (hasNumber(v.respiratory_rate) ? String(v.respiratory_rate) : null) },
      { getLabel: () => tm("form.mucousMembranes"), getValue: (v) => (hasText(v.mucous_membranes) ? v.mucous_membranes.trim() : null) },
      { getLabel: () => tm("form.crt"), getValue: (v) => (hasText(v.capillary_refill_time) ? v.capillary_refill_time.trim() : null) },
      { getLabel: () => tm("form.hydrationStatus"), getValue: (v) => (hasText(v.hydration_status) ? v.hydration_status.trim() : null) },
      { getLabel: () => tm("form.lymphNodes"), getValue: (v) => (hasText(v.lymph_nodes) ? v.lymph_nodes.trim() : null) },
      { getLabel: () => tm("form.cardiacAuscultation"), getValue: (v) => (hasText(v.cardiac_auscultation) ? v.cardiac_auscultation.trim() : null) },
      { getLabel: () => tm("form.respiratoryAuscultation"), getValue: (v) => (hasText(v.respiratory_auscultation) ? v.respiratory_auscultation.trim() : null) },
      { getLabel: () => tm("form.abdominalPalpation"), getValue: (v) => (hasText(v.abdominal_palpation) ? v.abdominal_palpation.trim() : null) },
      { getLabel: () => tm("form.locomotorSystem"), getValue: (v) => (hasText(v.locomotor_system) ? v.locomotor_system.trim() : null) },
      { getLabel: () => tm("form.skinCoat"), getValue: (v) => (hasText(v.skin_coat) ? v.skin_coat.trim() : null) },
      { getLabel: () => tm("form.otherSystems"), getValue: (v) => (hasText(v.other_systems) ? v.other_systems.trim() : null) },
    ],
  },
  {
    sectionTitleKey: "form.tabs.diagnosis",
    fields: [
      { getLabel: () => tm("form.differentialDiagnosis"), getValue: (v) => (hasText(v.presumptive_diagnosis) ? v.presumptive_diagnosis.trim() : null) },
      { getLabel: () => tm("form.definitiveDiagnosis"), getValue: (v) => (hasText(v.definitive_diagnosis) ? v.definitive_diagnosis.trim() : null) },
    ],
  },
  {
    sectionTitleKey: "form.tabs.treatment",
    fields: [
      { getLabel: () => tm("form.treatmentConduct"), getValue: (v) => (hasText(v.treatment) ? v.treatment.trim() : null) },
      {
        getLabel: () => tm("form.followUpDate"),
        getValue: (v) => {
          if (!v.follow_up_date) return null;
          return formatDateTime(v.follow_up_date instanceof Date ? v.follow_up_date : new Date(v.follow_up_date));
        },
      },
      { getLabel: () => tm("form.followUpNotes"), getValue: (v) => (hasText(v.follow_up_notes) ? v.follow_up_notes.trim() : null) },
    ],
  },
];

/** Texto do prontuário (rótulos + valores) para colar na área de transferência, no idioma atual. */
export function formatMedicalRecordClipboardText(values: MedicalRecordInput): string {
  const blocks: string[] = [];

  const consultDate = values.consultation_date
    ? formatDateTime(
        values.consultation_date instanceof Date ? values.consultation_date : new Date(values.consultation_date),
      )
    : null;
  const consultLine = consultDate ? fieldLine(tm("print.consultationDate"), consultDate) : null;
  if (consultLine) blocks.push(consultLine);

  for (const section of sections) {
    const lines: string[] = [];
    for (const f of section.fields) {
      const raw = f.getValue(values);
      if (raw === null) continue;
      const lbl = f.getLabel();
      const line = fieldLine(lbl, raw);
      if (line) lines.push(line);
    }
    if (lines.length > 0) {
      blocks.push([tm(section.sectionTitleKey), ...lines].join("\n"));
    }
  }

  if (values.register_death) {
    const deathBits: string[] = [];
    const regLine = fieldLine(tm("form.registerDeath"), tc("yes"));
    if (regLine) deathBits.push(regLine);
    if (values.death_date) {
      const d = values.death_date instanceof Date ? values.death_date : new Date(values.death_date);
      const ds = formatDateOnly(d);
      if (ds) {
        const dl = fieldLine(tm("form.deathDate"), ds);
        if (dl) deathBits.push(dl);
      }
    }
    if (hasText(values.death_cause)) {
      const cl = fieldLine(tm("form.deathCause"), values.death_cause.trim());
      if (cl) deathBits.push(cl);
    }
    if (deathBits.length > 0) {
      blocks.push(deathBits.join("\n\n"));
    }
  }

  return blocks.join("\n\n");
}
